import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';

export default class ModelLoader {
    constructor(loadingManager, renderer, cacheTimeout = 60000) {
        this.loadingManager = loadingManager;
        this.renderer = renderer; // Needed for KTX2Loader
        this.loader = new GLTFLoader(this.loadingManager);
        this.cacheTimeout = cacheTimeout;
        this.modelCache = {}; // Cache for loaded models
        this.lastAccessTime = {}; // To track last access time

        // Initialize DRACO Loader
        this.dracoLoader = new DRACOLoader();
        this.dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.4.3/');
        this.dracoLoader.preload();
        this.loader.setDRACOLoader(this.dracoLoader);

        // Initialize KTX2 Loader
        this.ktx2Loader = new KTX2Loader(this.loadingManager);
        this.loader.setKTX2Loader(this.ktx2Loader);
        this.ktx2Loader.setTranscoderPath('https://unpkg.com/three@0.157.0/examples/jsm/libs/basis/');
        //this.ktx2Loader.setTranscoderPath('https://unpkg.com/three@0.167.1/examples/jsm/libs/basis/');
        this.ktx2Loader.detectSupport(this.renderer);

        // Cache cleanup interval
        setInterval(() => this.clearCache(), cacheTimeout);
    }

    async loadModel(url) {
        // Return cached model if available
        if (this.modelCache[url]) {
            this.lastAccessTime[url] = Date.now();
            //console.log(`Returning cached model: ${url}`);
            return this.modelCache[url];
        }

        return new Promise((resolve, reject) => {
            this.loader.load(
                url,
                (gltf) => {
                    const model = gltf.scene;
                    /*
                                        // Process textures in the model
                                        model.traverse((child) => {
                                            if (child.isMesh && child.material) {
                                                this.processKTX2Textures(child.material);
                                            }
                                        });
                    */
                    // Cache the model
                    this.modelCache[url] = model;
                    this.lastAccessTime[url] = Date.now();
                    //console.log(`Model loaded: ${url}`);
                    resolve(model);
                },
                undefined,
                (error) => {
                    console.error(`Error loading model: ${url}`, error);
                    reject(error);
                }
            );
        });
    }

    async loadKTX2Texture(url) {
        return new Promise((resolve, reject) => {
            this.ktx2Loader.load(
                url,
                (texture) => resolve(texture),
                undefined,
                (error) => reject(error)
            );
        });
    }

    processKTX2Textures(material) {
        if (Array.isArray(material)) {
            material.forEach((mat) => this.replaceTextureWithKTX2(mat));
        } else {
            this.replaceTextureWithKTX2(material);
        }
    }

    async loadTexture(url) {
        return new Promise((resolve, reject) => {
            const loader = new THREE.TextureLoader();
            loader.load(
                url,
                (texture) => resolve(texture),
                undefined,
                (error) => reject(error)
            );
        });
    }

    replaceTextureWithKTX2(material) {
        if (material.map && material.map.name.endsWith('.ktx2')) {
            material.map = this.ktx2Loader.load(material.map.sourceFile, (texture) => {
                texture.needsUpdate = true;
            });
        }
    }

    clearCache() {
        const now = Date.now();
        Object.keys(this.lastAccessTime).forEach((url) => {
            if (now - this.lastAccessTime[url] > this.cacheTimeout) {
                //console.log(`Clearing cache for model: ${url}`);
                delete this.modelCache[url];
                delete this.lastAccessTime[url];
            }
        });
    }
}

/*
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { KTX2Loader } from "three/examples/jsm/Addons.js";

export default class ModelLoader {
    constructor(loadingManager, cacheTimeout = 60000) {
        this.loadingManager = loadingManager;
        this.loader = new GLTFLoader(this.loadingManager);
        this.cacheTimeout = cacheTimeout;
        this.modelCache = {}; // To store the loaded models
        this.lastAccessTime = {}; // To track the last access time of the models
        this.dracoLoader = new DRACOLoader();
        this.dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.4.3/'); // Path to Draco decoder
        this.loader.setDRACOLoader(this.dracoLoader);

        // Clean up the cache after a certain time period
        setInterval(() => this.clearCache(), cacheTimeout);
    }

    async loadModel(url) {
        // Check if the model is already in the cache
        if (this.modelCache[url]) {
            // Update the last access time and return the cached model
            this.lastAccessTime[url] = Date.now();
            console.log(`Returning cached model: ${url}`);
            return this.modelCache[url];
        }

        return new Promise((resolve, reject) => {
            this.loader.setDRACOLoader(this.dracoLoader);


            this.loader.load(
                url,
                (gltf) => {
                    // Store the loaded model in the cache
                    this.modelCache[url] = gltf.scene;
                    this.lastAccessTime[url] = Date.now();
                    console.log(`Model loaded: ${url}`);
                    resolve(gltf.scene);
                },
                undefined, // onProgress callback is not needed for now
                (error) => {
                    console.error(`Error loading model: ${url}`, error);
                    reject(error);
                }
            );
        });

    }

    // Function to clear the cache
    clearCache() {
        const now = Date.now();
        Object.keys(this.lastAccessTime).forEach((url) => {
            // If a model hasn't been accessed in a certain period of time, remove it from cache
            if (now - this.lastAccessTime[url] > this.cacheTimeout) {
                console.log(`Clearing cache for model: ${url}`);
                delete this.modelCache[url];
                delete this.lastAccessTime[url];
            }
        });
    }
}
    */